import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { useLang, tx } from "@/context/LangContext";
import { DAVIDO, PROJECTS, T } from "@/data/content";
import { Portal } from "@/components/Reveal";
import ProjectModal from "@/components/ProjectModal";
import { scrollToId } from "@/lib/scroll";
import { Volume2, VolumeX } from "lucide-react";
import { music } from "@/lib/player";

// action per card: video projects open the player, photo projects scroll to their gallery
const CARDS = [
    { img: "/videos/timelease-reel.jpg", label: "TIMELEASE", x: 12, y: 18, w: 300, projectId: "timelease-reel", cursor: "PLAY" },
    { img: "/img/laduree-01.jpg", label: "LADURÉE", x: 62, y: 12, w: 340, target: "#laduree", cursor: "VIEW" },
    { img: "/img/overdyed-01.jpg", label: "OVERDYED", x: 20, y: 52, w: 260, target: "[data-testid='photo-group-overdyed']", cursor: "VIEW" },
    { img: "/videos/davido-concert-live.jpg", label: "DAVIDO — LIVE", x: 58, y: 46, w: 320, davido: true, cursor: "PLAY" },
    { img: "/img/esquimonoir-01.jpg", label: "ESQUIMONOIR", x: 36, y: 14, w: 280, target: "[data-testid='photo-group-esquimonoir']", cursor: "VIEW" },
    { img: "/img/kallykurls-01.jpg", label: "KALLY KURLS", x: 66, y: 56, w: 280, target: "[data-testid='photo-group-kallykurls']", cursor: "VIEW" },
    { img: "/videos/clip-nezma.jpg", label: "NEZMA — JSPCF", x: 10, y: 40, w: 300, projectId: "clip-nezma", cursor: "PLAY" },
    { img: "/img/car-gt3rs-1.webp", label: "CARS", x: 46, y: 22, w: 330, target: "[data-testid='photo-group-automotive']", cursor: "VIEW" },
    { img: "/img/pnasty-01.jpg", label: "PNASTY", x: 26, y: 60, w: 270, target: "[data-testid='photo-group-pnasty']", cursor: "VIEW" },
    // — nouveaux contenus ajoutés : la traversée continue jusqu'au header —
    { img: "/img/instants-figes/DSC8851.jpg", label: "629LA — INSTANTS FIGÉS", x: 56, y: 14, w: 300, target: "[data-testid='photo-group-instants-figes']", cursor: "VIEW" },
    { img: "/video/6xo/posters/DA-MOVIE.jpg", label: "6XO — DA MOVIE", x: 14, y: 46, w: 320, creator: true, cursor: "PLAY" },
    { img: "/img/6xo/zve6729.jpg", label: "6XO — PHOTO", x: 62, y: 48, w: 270, creator: true, cursor: "VIEW" },
    { img: "/img/instants-figes/DSC8938.jpg", label: "629LA — INSTANTS FIGÉS", x: 30, y: 18, w: 260, target: "[data-testid='photo-group-instants-figes']", cursor: "VIEW" },
    { img: "/video/6xo/posters/nore6l-final.jpg", label: "6XO — NORE6L", x: 52, y: 56, w: 300, creator: true, cursor: "PLAY" },
    { img: "/img/instants-figes/DSC9072.jpg", label: "629LA — INSTANTS FIGÉS", x: 18, y: 26, w: 280, target: "[data-testid='photo-group-instants-figes']", cursor: "VIEW" },
    { img: "/img/6xo/zve7383.jpg", label: "6XO — PHOTO", x: 44, y: 12, w: 260, creator: true, cursor: "VIEW" },
];

const Card = ({ c, i, progress, onOpen }) => {
    // timing séquentiel : chaque carte traverse l'écran pendant SA tranche de scroll
    // → une carte à la fois (léger chevauchement de transition), aucune zone vide à la fin
    const N = CARDS.length;
    const t0 = i / N;
    const span = 2.4 / N; // durée de vol d'une carte (fraction du scroll total)
    const t1 = Math.min(1, t0 + span);
    const z = useTransform(progress, [t0, t1], [-1500, 1100], { clamp: true });
    const opacity = useTransform(z, [-1500, -950, 150, 700], [0, 1, 1, 0]);
    return (
        <motion.button
            data-testid={`immersive-card-${i}`}
            data-cursor={c.cursor}
            onClick={() => onOpen(c)}
            className="group absolute block overflow-hidden text-left shadow-[0_30px_80px_rgba(0,0,0,0.65)] transition-[outline] duration-300 hover:outline hover:outline-2 hover:outline-violet/70"
            style={{
                left: `${c.x}%`,
                top: `${c.y}%`,
                width: `min(${c.w}px, 42vw)`,
                z,
                opacity,
                transformStyle: "preserve-3d",
            }}
        >
            <img src={c.img} alt={c.label} loading="lazy" className="block aspect-video w-full object-cover" />
            <p className="absolute bottom-2 left-3 font-mono text-[9px] tracking-[0.3em] text-cream/90 [text-shadow:0_1px_8px_rgba(0,0,0,0.9)]">
                {c.label}
            </p>
        </motion.button>
    );
};


export default function Immersive() {
    const { lang } = useLang();
    const [active, setActive] = useState(null);
    const [sound, setSound] = useState(music.state);
    useEffect(() => music.subscribe(setSound), []);
    const soundOn = sound.status === "playing";
    const secRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: secRef,
        offset: ["start start", "end end"],
    });

    // giant typography drifting in depth
    const titleZ = useTransform(scrollYProgress, [0, 1], [-2200, 500]);
    const titleOpacity = useTransform(scrollYProgress, [0, 0.15, 0.85, 1], [0, 0.5, 0.5, 0]);
    const glow = useTransform(scrollYProgress, [0, 0.5, 1], [0.25, 0.6, 0.9]);
    const hintOpacity = useTransform(scrollYProgress, [0, 0.08, 0.22], [1, 1, 0]);

    const openCard = (c) => {
        if (c.projectId) {
            const p = PROJECTS.find((pr) => pr.id === c.projectId);
            if (p) setActive(p);
            return;
        }
        if (c.davido) {
            setActive({ ...DAVIDO.featured, desc: DAVIDO.featured.desc });
            return;
        }
        if (c.creator) {
            // carte 6xo → page Artistes, directement sur son profil
            window.__scrollToCreator = true;
            window.__goPage?.("artiste");
            return;
        }
        if (c.target) scrollToId(c.target, -90);
    };

    return (
        <section
            ref={secRef}
            data-testid="immersive-section"
            className="relative h-[430vh] bg-ink"
        >
            <div className="sticky top-0 h-screen overflow-hidden" style={{ perspective: "900px" }}>
                {/* ambient glow */}
                <motion.div
                    className="absolute inset-0"
                    style={{
                        opacity: glow,
                        background:
                            "radial-gradient(55% 45% at 50% 55%, rgba(138,43,226,0.22) 0%, transparent 65%), radial-gradient(40% 35% at 70% 30%, rgba(212,175,55,0.12) 0%, transparent 70%)",
                    }}
                />
                {/* giant depth typography + mirror */}
                <motion.div
                    className="absolute inset-0 flex flex-col items-center justify-center"
                    style={{ z: titleZ, opacity: titleOpacity, transformStyle: "preserve-3d" }}
                >
                    <span className="select-none font-display text-[22vw] font-extrabold uppercase leading-[0.8] tracking-[-0.04em] text-transparent [-webkit-text-stroke:2px_rgba(242,239,233,0.35)]">
                        GLOOM
                    </span>
                    <span
                        aria-hidden="true"
                        className="select-none font-display text-[22vw] font-extrabold uppercase leading-[0.8] tracking-[-0.04em] text-transparent opacity-30 [-webkit-text-stroke:2px_rgba(139,92,246,0.5)] [transform:scaleY(-1)]"
                    >
                        GLOOM
                    </span>
                </motion.div>
                {/* floating project cards */}
                {CARDS.map((c, i) => (
                    <Card key={`${c.label}-${i}`} c={c} i={i} progress={scrollYProgress} onOpen={openCard} />
                ))}
                {/* corner label */}
                <div className="absolute left-5 top-24 md:left-10">
                    <p className="font-mono text-[10px] tracking-[0.35em] text-violet">
                        {tx(lang, T.immersive.label)}
                    </p>
                </div>
                {/* sound toggle — drives the global Gloom Radio player */}
                <button
                    data-testid="tunnel-sound-btn"
                    data-cursor={soundOn ? "OFF" : "SON"}
                    onClick={() => music.toggle()}
                    aria-pressed={soundOn}
                    className={`absolute right-5 top-24 flex items-center gap-2 border px-3 py-2 font-mono text-[9px] tracking-[0.3em] backdrop-blur-sm transition-all duration-500 md:right-10 ${
                        soundOn
                            ? "border-blood/70 bg-blood/15 text-cream"
                            : "border-white/20 bg-ink/40 text-cream/60 hover:border-cream hover:text-cream"
                    }`}
                >
                    {soundOn ? <Volume2 size={12} /> : <VolumeX size={12} />}
                    {soundOn ? tx(lang, T.immersive.soundOn) : tx(lang, T.immersive.soundOff)}
                </button>
                {/* scroll cue — the floating arrow is global (FloatingArrow) */}
                <motion.div
                    style={{ opacity: hintOpacity }}
                    className="pointer-events-none absolute inset-0 flex flex-col items-center justify-end pb-16"
                    data-testid="tunnel-scroll-hint"
                >
                    <div className="flex flex-col items-center gap-2">
                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 2.2, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                            className="font-mono text-[10px] tracking-[0.4em] text-cream/70 md:text-[11px]"
                        >
                            {tx(lang, T.immersive.hint)}
                        </motion.p>
                        <motion.span
                            aria-hidden="true"
                            animate={{ y: [0, 8, 0], opacity: [0.35, 0.9, 0.35] }}
                            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
                            className="mt-1 block h-8 w-px bg-gradient-to-b from-violet to-transparent"
                        />
                    </div>
                </motion.div>
            </div>

            <Portal>
                <AnimatePresence>
                    {active && (
                        <ProjectModal project={active} onClose={() => setActive(null)} />
                    )}
                </AnimatePresence>
            </Portal>
        </section>
    );
}
